/**
 * Footer.jsx - Website Footer Component
 * 
 * A responsive footer component that appears at the bottom of every page.
 * Features include:
 * - Company information and logo
 * - Quick navigation links
 * - Services list
 * - Contact information with icons
 * - Copyright notice
 * - Smooth animations using Framer Motion
 * 
 * Dependencies:
 * - react: Core React library
 * - react-router-dom: For client-side navigation links
 * - framer-motion: For smooth animations
 * - Bootstrap: For responsive grid layout and styling
 * - Bootstrap Icons: For decorative icons
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaClock, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import './Footer.css'; // Component-specific styles
import Logo from '../assets/Images/Logo.png'; // Company logo image

/**
 * Footer Component
 * 
 * The main footer component that displays site-wide information and navigation.
 * Uses Framer Motion for staggered animations of footer sections.
 */
const Footer = () => {
  // Get current year for copyright notice
  const currentYear = new Date().getFullYear();

  // Animation configuration for Framer Motion
  const fadeInUp = {
    initial: { y: 30, opacity: 0 }, // Start slightly below and transparent
    animate: { y: 0, opacity: 1 },   // Animate to normal position and visible
    transition: { duration: 0.5 }    // Animation duration
  };

  return (
    <footer className="footer">
      <div className="container">
        {/* Footer content grid */}
        <div className="row g-4">
          {/* Company Information Column */}
          <motion.div 
            className="col-lg-4 col-md-6 footer-col footer-about"
            {...fadeInUp} // Apply fade-in animation
          >
            {/* Company Logo */}
            <div className="footer-logo-container mb-4">
              <div className="footer-logo-wrapper">
                <img 
                  src={Logo} 
                  alt="General Pest Control Services Logo" 
                  className="footer-logo img-fluid" 
                />
              </div>
            </div>
            {/* Company Address */}
            <p className="footer-description">
              Address: 7L44 Vrindawan Society, Kudi, Kudi Bhagtasni Housing Board, 
              Jodhpur, Rajasthan 342005
            </p>
          </motion.div>

          {/* Quick Navigation Links Column */}
          <motion.div 
            className="col-lg-2 col-md-6 footer-col"
            {...fadeInUp}
            transition={{ delay: 0.1 }} // Slight delay for staggered animation
          >
            <h4>Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </motion.div>

          {/* Services List Column */}
          <motion.div 
            className="col-lg-3 col-md-6 footer-col"
            {...fadeInUp}
            transition={{ delay: 0.2 }} // Increased delay for staggered effect
          >
            <h4>Our Services</h4>
            <ul className="footer-links">
              <li><Link to="/services">Termite Control / Poross Pipe</Link></li>
              <li><Link to="/services">Cockroach Control</Link></li>
              <li><Link to="/services">Rodent Control</Link></li>
              <li><Link to="/services">Bed Bug Control</Link></li>
              <li><Link to="/services">Mosquito Control</Link></li>
              <li><Link to="/services">Ant Control</Link></li>
              <li><Link to="/services">General Pest Control</Link></li>
              <li><Link to="/services">Lizard Control</Link></li>
              <li><Link to="/services">Fly Control</Link></li>
            </ul>
          </motion.div>

          {/* Contact Information Column */}
          <motion.div 
            className="col-lg-3 col-md-6 footer-col"
            {...fadeInUp}
            transition={{ delay: 0.3 }} // Further delay for staggered effect
          >
            <h4>Contact Info</h4>
            <div className="footer-contact">
              {/* Address */}
              <div className="footer-contact-item">
                <i className="bi bi-geo-alt-fill"></i>
                <div>
                  Kudi Bhagtasni Housing Board, Jodhpur,
                  <br />Rajasthan, India
                </div>
              </div>
              
              {/* Phone Numbers */}
              <div className="footer-contact-item">
                <i className="bi bi-telephone-fill"></i>
                <div>+91 9587425595</div>
                <div>+91 7522053432</div>
              </div>
              
              {/* Email */}
              <div className="footer-contact-item">
                <i className="bi bi-envelope-fill"></i>
                <a 
                  href="mailto:Generalpestcontrolservicesjodh@gmail.com" 
                  className="gmailId"
                >
                  Generalpestcontrolservicesjodh@gmail.com
                </a>
              </div>
              
              {/* Business Hours */}
              <div className="footer-contact-item">
                <i className="bi bi-clock-fill"></i>
                <div>
                  Mon - Sat: 24 Hours 
                  <br />Sunday: Half Day
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="footer-bottom">
        <div className="text-center">
          <p>&copy; {currentYear} General Pest Control Services | All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
